import { authenticatedApiRequest, cleanEmptyFields } from "./core";
import type { PaginatedResponse } from "./core";
import type {
  ContractOut,
  ContractOutDetails,
  CreateContractOut,
  CreateLicensee,
  Licensee,
  LicenseeLicensedTrack,
} from "@/types";

export interface ContractsOutQueryParams {
  label_id?: string;
  page?: number;
  page_size?: number;
  q?: string;
  ordering?: string;
  /** Comma-separated contract statuses. */
  status?: string;
  /** Comma-separated licensing types. */
  licensing_type?: string;
  /** Comma-separated licensee names — the one thing both variants agree on. */
  licensee?: string;
  /** Only the contracts out licensing tracks of this contract in. */
  contract_in?: string;
}

export interface PaginatedContractsOutResponse extends PaginatedResponse {
  results: ContractOut[];
}

export interface PaginatedLicenseesResponse extends PaginatedResponse {
  results: Licensee[];
}

const basePath = "/contracts/out";
const licenseesPath = "/contracts/licensees";

function toQueryString(filters?: object): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters as Record<string, unknown>)) {
    if (value === undefined || value === null || value === "") continue;
    params.append(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const contractsOutApi = {
  getContractsOut: async (
    filters?: ContractsOutQueryParams,
  ): Promise<PaginatedContractsOutResponse> => {
    return authenticatedApiRequest<PaginatedContractsOutResponse>(
      `${basePath}${toQueryString(filters)}`,
    );
  },
  getContractOut: async (id: string): Promise<ContractOutDetails> => {
    return authenticatedApiRequest<ContractOutDetails>(`${basePath}/${id}`);
  },
  createContractOut: async (contractOut: CreateContractOut): Promise<ContractOut> => {
    return authenticatedApiRequest<ContractOut>(basePath, {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(contractOut)),
    });
  },
  patchContractOut: async (
    id: string,
    updates: Partial<CreateContractOut>,
  ): Promise<ContractOut> => {
    return authenticatedApiRequest<ContractOut>(`${basePath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },
  deleteContractOut: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`${basePath}/${id}`, { method: "DELETE" });
  },
  /**
   * Save a contract out's PDF. The file travels as a data URL rather than
   * multipart: the prototype has no file storage behind it, so the PDF has to
   * come back from the same place it was saved to.
   */
  uploadFile: async (id: string, file: File): Promise<ContractOut> => {
    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error("This prototype stores the PDF in the browser — pick a file under 3MB");
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("Could not read the file"));
      reader.readAsDataURL(file);
    });
    return authenticatedApiRequest<ContractOut>(`${basePath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ file: dataUrl, file_name: file.name }),
    });
  },
  getLicensees: async (filters?: {
    label_id?: string;
    page?: number;
    page_size?: number;
    q?: string;
    ordering?: string;
  }): Promise<PaginatedLicenseesResponse> => {
    return authenticatedApiRequest<PaginatedLicenseesResponse>(
      `${licenseesPath}${toQueryString(filters)}`,
    );
  },
  getLicensee: async (id: string): Promise<Licensee> => {
    return authenticatedApiRequest<Licensee>(`${licenseesPath}/${id}`);
  },
  createLicensee: async (licensee: CreateLicensee & { label: string }): Promise<Licensee> => {
    return authenticatedApiRequest<Licensee>(licenseesPath, {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(licensee)),
    });
  },
  /** Every track this licensee holds, across all of its contracts out. */
  getLicenseeLicensedTracks: async (id: string): Promise<LicenseeLicensedTrack[]> => {
    return authenticatedApiRequest<LicenseeLicensedTrack[]>(
      `${licenseesPath}/${id}/licensed-tracks`,
    );
  },
  updateLicensee: async (id: string, licensee: Partial<CreateLicensee>): Promise<Licensee> => {
    return authenticatedApiRequest<Licensee>(`${licenseesPath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(licensee),
    });
  },
  deleteLicensee: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`${licenseesPath}/${id}`, { method: "DELETE" });
  },
};
